import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, FileText, CreditCard, Activity, MessageSquare, Download, CheckCircle2, ChevronRight, User, Send, Mail, Phone, MapPin } from 'lucide-react';
import { SimonsHeader, SimonsFooter } from '../components/SimonsNavigation';
export function SimonsPortal({
  onBack,
  setActivePage
}: {
  onBack: () => void;
  setActivePage: (p: any) => void;
}) {
  const [activeTab, setActiveTab] = useState<'profile' | 'contract' | 'messaging' | 'billing'>('profile');
  const [message, setMessage] = useState('');
  return <div className="min-h-screen bg-[#F3F6F9] font-sans" data-id="element-2084">
      <SimonsHeader activePage="portal" setActivePage={setActivePage} data-id="element-2085" />

      <main className="max-w-7xl mx-auto p-8" data-id="element-2086">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-10" data-id="element-2087">
          <h1 className="text-3xl font-black text-[#002366] uppercase tracking-tight" data-id="element-2088">
            Secure Partner Portal
          </h1>
          <p className="text-slate-500 font-medium" data-id="element-2089">
            Manage your profile, insurance contracts, and communication in one
            secure environment.
          </p>
        </motion.div>

        {/* Quick Stats */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-2090">
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between" data-id="element-2091">
            <div data-id="element-2092">
              <p className="text-[10px] font-black uppercase text-slate-400 mb-1" data-id="element-2093">
                Account Status
              </p>
              <p className="text-lg font-black text-[#002366]" data-id="element-2094">
                Verified Partner
              </p>
            </div>
            <div className="w-10 h-10 bg-teal-50 rounded-full flex items-center justify-center text-teal-600" data-id="element-2095">
              <CheckCircle2 size={20} data-id="element-2096" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between" data-id="element-2097">
            <div data-id="element-2098">
              <p className="text-[10px] font-black uppercase text-slate-400 mb-1" data-id="element-2099">
                Active Contracts
              </p>
              <p className="text-lg font-black text-[#002366]" data-id="element-2100">2 Policies</p>
            </div>
            <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center text-[#002366]" data-id="element-2101">
              <Shield size={20} data-id="element-2102" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between" data-id="element-2103">
            <div data-id="element-2104">
              <p className="text-[10px] font-black uppercase text-slate-400 mb-1" data-id="element-2105">
                Unread Messages
              </p>
              <p className="text-lg font-black text-[#002366]" data-id="element-2106">1 New</p>
            </div>
            <div className="w-10 h-10 bg-[#FFD700]/10 rounded-full flex items-center justify-center text-[#002366]" data-id="element-2107">
              <MessageSquare size={20} data-id="element-2108" />
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-8" data-id="element-2109">
          {/* Sidebar Tabs */}
          <div className="space-y-2" data-id="element-2110">
            {[{
            id: 'profile',
            label: 'My Profile',
            icon: User
          }, {
            id: 'contract',
            label: 'Insurance Contract',
            icon: FileText
          }, {
            id: 'messaging',
            label: 'Message Agent',
            icon: MessageSquare
          }, {
            id: 'billing',
            label: 'Manage Billing',
            icon: CreditCard
          }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`w-full flex items-center gap-3 px-6 py-4 rounded-xl font-bold text-sm transition-all ${activeTab === tab.id ? 'bg-[#002366] text-white shadow-lg' : 'bg-white text-slate-400 hover:bg-slate-50'}`} data-id="element-2111">
                <tab.icon size={18} data-id="element-2112" />
                {tab.label}
              </button>)}
          </div>

          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 min-h-[600px]" data-id="element-2113">
            {activeTab === 'profile' && <div className="space-y-8" data-id="element-2114">
                <div className="flex justify-between items-center" data-id="element-2115">
                  <h3 className="text-2xl font-black text-[#002366]" data-id="element-2116">
                    My Profile
                  </h3>
                  <button onClick={() => alert('Edit profile functionality coming soon.')} className="px-4 py-2 bg-slate-100 rounded-lg text-xs font-black uppercase text-[#002366] hover:bg-slate-200" data-id="element-2117">
                    Edit Profile
                  </button>
                </div>
                <div className="flex flex-col md:flex-row gap-12 items-start" data-id="element-2118">
                  <div className="w-32 h-32 rounded-3xl bg-slate-100 overflow-hidden border-4 border-white shadow-lg" data-id="element-2119">
                    <img src="https://i.pravatar.cc/150?img=33" alt="Profile" className="w-full h-full object-cover" data-id="element-2120" />
                  </div>
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2121">
                    <div className="space-y-1" data-id="element-2122">
                      <p className="text-[10px] font-black uppercase text-slate-400" data-id="element-2123">
                        Full Name
                      </p>
                      <p className="text-lg font-bold text-[#002366]" data-id="element-2124">
                        John Investor
                      </p>
                    </div>
                    <div className="space-y-1" data-id="element-2125">
                      <p className="text-[10px] font-black uppercase text-slate-400" data-id="element-2126">
                        Email Address
                      </p>
                      <p className="text-lg font-bold text-[#002366]" data-id="element-2127">
                        john.investor@email.com
                      </p>
                    </div>
                    <div className="space-y-1" data-id="element-2128">
                      <p className="text-[10px] font-black uppercase text-slate-400" data-id="element-2129">
                        Phone Number
                      </p>
                      <p className="text-lg font-bold text-[#002366]" data-id="element-2130">
                        +1 (555) 123-4567
                      </p>
                    </div>
                    <div className="space-y-1" data-id="element-2131">
                      <p className="text-[10px] font-black uppercase text-slate-400" data-id="element-2132">
                        Mailing Address
                      </p>
                      <p className="text-lg font-bold text-[#002366]" data-id="element-2133">
                        123 Strategic Way, Suite 100, NY 10001
                      </p>
                    </div>
                  </div>
                </div>
                <div className="pt-8 border-t border-slate-100" data-id="element-2134">
                  <h4 className="text-sm font-black uppercase text-slate-400 mb-4" data-id="element-2135">
                    Security Settings
                  </h4>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl" data-id="element-2136">
                    <div className="flex items-center gap-3" data-id="element-2137">
                      <Shield size={20} className="text-[#002366]" data-id="element-2138" />
                      <span className="text-sm font-bold" data-id="element-2139">
                        Two-Factor Authentication
                      </span>
                    </div>
                    <div className="w-10 h-5 bg-teal-500 rounded-full relative" data-id="element-2140">
                      <div className="absolute right-1 top-1 w-3 h-3 bg-white rounded-full" data-id="element-2141"></div>
                    </div>
                  </div>
                </div>
              </div>}

            {activeTab === 'contract' && <div className="space-y-8" data-id="element-2142">
                <div className="flex justify-between items-center" data-id="element-2143">
                  <h3 className="text-2xl font-black text-[#002366]" data-id="element-2144">
                    Insurance Contracts
                  </h3>
                  <button onClick={() => setActivePage('quote')} className="px-4 py-2 bg-[#002366] text-white rounded-lg text-xs font-black uppercase tracking-widest hover:bg-[#001a4d]" data-id="element-2145">
                    Add New Policy
                  </button>
                </div>
                <div className="space-y-6" data-id="element-2146">
                  {[{
                id: 'SA-98234-COMM',
                type: 'Commercial Portfolio',
                status: 'Active',
                renewal: 'Jan 01, 2027'
              }, {
                id: 'SA-98234-AUTO',
                type: 'Commercial Auto Fleet',
                status: 'Active',
                renewal: 'Dec 15, 2026'
              }].map(policy => <div key={policy.id} className="p-6 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col md:flex-row justify-between gap-6" data-id="element-2147">
                      <div className="flex items-start gap-4" data-id="element-2148">
                        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-[#002366] shadow-sm" data-id="element-2149">
                          <FileText size={24} data-id="element-2150" />
                        </div>
                        <div data-id="element-2151">
                          <p className="text-lg font-black text-[#002366]" data-id="element-2152">
                            {policy.type}
                          </p>
                          <p className="text-sm text-slate-500" data-id="element-2153">
                            Policy ID: {policy.id}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col md:items-end justify-center gap-2" data-id="element-2154">
                        <span className="px-3 py-1 bg-teal-50 text-teal-600 text-[10px] font-black uppercase rounded-full border border-teal-100 w-fit" data-id="element-2155">
                          {policy.status}
                        </span>
                        <p className="text-xs text-slate-400 font-bold" data-id="element-2156">
                          Renewal: {policy.renewal}
                        </p>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-2157">
                        <button onClick={() => alert('Download starting...')} className="p-3 bg-white text-[#002366] rounded-xl border border-slate-200 hover:bg-slate-50 transition-all" data-id="element-2158">
                          <Download size={18} data-id="element-2159" />
                        </button>
                        <button onClick={() => alert('Policy details coming soon.')} className="px-4 py-3 bg-[#002366] text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-[#001a4d] transition-all" data-id="element-2160">
                          View Details
                        </button>
                      </div>
                    </div>)}
                </div>
              </div>}

            {activeTab === 'messaging' && <div className="space-y-8 h-full flex flex-col" data-id="element-2161">
                <div className="flex justify-between items-center" data-id="element-2162">
                  <h3 className="text-2xl font-black text-[#002366]" data-id="element-2163">
                    Message Agent
                  </h3>
                  <div className="flex items-center gap-3" data-id="element-2164">
                    <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white shadow-sm" data-id="element-2165">
                      <img src="https://i.pravatar.cc/100?img=12" alt="Agent" className="w-full h-full object-cover" data-id="element-2166" />
                    </div>
                    <div data-id="element-2167">
                      <p className="text-xs font-black text-[#002366]" data-id="element-2168">
                        Simons Acquah
                      </p>
                      <p className="text-[10px] text-teal-600 font-bold uppercase" data-id="element-2169">
                        Online
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex-1 bg-slate-50 rounded-3xl p-6 overflow-y-auto space-y-4 min-h-[300px]" data-id="element-2170">
                  <div className="flex justify-start" data-id="element-2171">
                    <div className="max-w-[80%] bg-white p-4 rounded-2xl rounded-tl-none shadow-sm border border-slate-100" data-id="element-2172">
                      <p className="text-sm text-[#002366] font-medium" data-id="element-2173">
                        Hello John! I've reviewed your recent policy update.
                        Everything looks strategic. Do you have any other
                        questions?
                      </p>
                      <p className="text-[10px] text-slate-400 mt-2 font-bold uppercase" data-id="element-2174">
                        10:45 AM
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end" data-id="element-2175">
                    <div className="max-w-[80%] bg-[#002366] p-4 rounded-2xl rounded-tr-none shadow-lg text-white" data-id="element-2176">
                      <p className="text-sm font-medium" data-id="element-2177">
                        Thanks Simons! I'm looking to add another property to
                        the portfolio next month. What's the best way to start?
                      </p>
                      <p className="text-[10px] text-white/40 mt-2 font-bold uppercase text-right" data-id="element-2178">
                        11:02 AM
                      </p>
                    </div>
                  </div>
                </div>

                <div className="relative" data-id="element-2179">
                  <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Type your message to Simons..." className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 pr-16 focus:outline-none focus:border-[#002366] min-h-[100px] resize-none" data-id="element-2180" />
                  <button className="absolute bottom-4 right-4 p-3 bg-[#002366] text-white rounded-xl hover:bg-[#001a4d] transition-all shadow-lg" data-id="element-2181">
                    <Send size={20} data-id="element-2182" />
                  </button>
                </div>
              </div>}

            {activeTab === 'billing' && <div className="space-y-8" data-id="element-2183">
                <h3 className="text-2xl font-black text-[#002366]" data-id="element-2184">
                  Manage Billing
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2185">
                  <div className="p-8 bg-[#002366] text-white rounded-[32px] shadow-2xl relative overflow-hidden" data-id="element-2186">
                    <div className="relative z-10" data-id="element-2187">
                      <div className="flex justify-between items-start mb-12" data-id="element-2188">
                        <CreditCard size={32} className="text-[#FFD700]" data-id="element-2189" />
                        <span className="text-xs font-black uppercase tracking-widest text-white/40" data-id="element-2190">
                          Primary Card
                        </span>
                      </div>
                      <p className="text-xl font-bold tracking-[0.2em] mb-8" data-id="element-2191">
                        •••• •••• •••• 4242
                      </p>
                      <div className="flex justify-between items-end" data-id="element-2192">
                        <div data-id="element-2193">
                          <p className="text-[10px] font-black uppercase text-white/40 mb-1" data-id="element-2194">
                            Card Holder
                          </p>
                          <p className="text-sm font-bold" data-id="element-2195">JOHN INVESTOR</p>
                        </div>
                        <div data-id="element-2196">
                          <p className="text-[10px] font-black uppercase text-white/40 mb-1" data-id="element-2197">
                            Expires
                          </p>
                          <p className="text-sm font-bold" data-id="element-2198">12/28</p>
                        </div>
                      </div>
                    </div>
                    <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/5 rounded-full blur-3xl" data-id="element-2199"></div>
                  </div>

                  <div className="space-y-4" data-id="element-2200">
                    <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100" data-id="element-2201">
                      <p className="text-[10px] font-black uppercase text-slate-400 mb-2" data-id="element-2202">
                        Billing Address
                      </p>
                      <p className="text-sm font-bold text-[#002366]" data-id="element-2203">
                        123 Strategic Way, Suite 100, New York, NY 10001
                      </p>
                      <button onClick={() => alert('Update address functionality coming soon.')} className="mt-4 text-xs font-black text-[#002366] hover:underline" data-id="element-2204">
                        Update Address
                      </button>
                    </div>
                    <button onClick={() => alert('Add payment method functionality coming soon.')} className="w-full py-4 border-2 border-dashed border-slate-200 rounded-2xl text-slate-400 font-bold text-sm hover:border-[#002366] hover:text-[#002366] transition-all flex items-center justify-center gap-2" data-id="element-2205">
                      + Add Payment Method
                    </button>
                  </div>
                </div>

                <div className="pt-8 border-t border-slate-100" data-id="element-2206">
                  <h4 className="text-sm font-black uppercase tracking-widest text-slate-400 mb-6" data-id="element-2207">
                    Recent Transactions
                  </h4>
                  <div className="space-y-4" data-id="element-2208">
                    {[{
                  date: 'May 15, 2026',
                  amount: '$450.00',
                  status: 'Paid'
                }, {
                  date: 'Apr 15, 2026',
                  amount: '$450.00',
                  status: 'Paid'
                }].map((t, i) => <div key={i} className="flex justify-between items-center py-3 border-b border-slate-50 last:border-0" data-id="element-2209">
                        <div data-id="element-2210">
                          <p className="text-sm font-bold text-[#002366]" data-id="element-2211">
                            {t.date}
                          </p>
                          <p className="text-[10px] text-slate-400 font-bold uppercase" data-id="element-2212">
                            Monthly Premium
                          </p>
                        </div>
                        <div className="text-right" data-id="element-2213">
                          <p className="text-sm font-black text-[#002366]" data-id="element-2214">
                            {t.amount}
                          </p>
                          <p className="text-[10px] font-black text-teal-600 uppercase" data-id="element-2215">
                            {t.status}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>}
          </div>
        </motion.div>
      </main>

      <SimonsFooter data-id="element-2216" />
    </div>;
}